import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressBar, Typography } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import type { CatalogImportBlocker } from '@/pages/catalogImport/store';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/** What the line under the buttons says: why Import is disabled, or that nothing is saved yet. */
const HELPER_KEY = {
    importing: 'catalogImport.products.bar.helper.importing',
    checking: 'catalogImport.products.bar.helper.checking',
    unchecked: 'catalogImport.products.bar.helper.unchecked',
    needsAttention: 'catalogImport.products.bar.helper.needsAttention',
    nothingReady: 'catalogImport.products.bar.helper.nothingReady',
    ready: 'catalogImport.products.bar.helper.ready',
    suggestedFixes: 'catalogImport.products.bar.helper.suggestedFixes',
} as const satisfies Record<
    CatalogImportBlocker | 'ready' | 'suggestedFixes',
    string
>;

type ActionBarHelperProps = {
    /** The helper line's id: it describes Import and Import with suggested fixes. */
    id: string;
    /** Why Import is disabled; `null` when it is enabled. */
    blocker: CatalogImportBlocker | null;
    /** Rows the user included since a check left them out, with no answer yet. */
    uncheckedCount: number;
    /** The line also describes Import with suggested fixes. */
    describesSuggestedFixes: boolean;
    /** "Importing N rows…", which names the progress bar. */
    importingMessage: string;
};

/**
 * The action bar's helper: while the import runs an indeterminate progress
 * bar, then the line that says why Import is disabled (or that nothing is
 * saved until the import), followed, while the suggested fixes are offered,
 * by what that action does.
 */
export const ActionBarHelper: React.FC<ActionBarHelperProps> = ({
    id,
    blocker,
    uncheckedCount,
    describesSuggestedFixes,
    importingMessage,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:min-w-64">
            {blocker === 'importing' ? (
                <ProgressBar
                    isIndeterminate
                    aria-label={importingMessage}
                    size="sm"
                    {...getNodeTestIdProp('catalog-import-action-bar-progress')}
                >
                    <ProgressBar.Track>
                        <ProgressBar.Fill />
                    </ProgressBar.Track>
                </ProgressBar>
            ) : null}
            <Typography
                className="sm:text-end"
                color="muted"
                id={id}
                type="body-xs"
                {...getNodeTestIdProp('catalog-import-action-bar-helper')}
            >
                {blocker === 'unchecked'
                    ? t(HELPER_KEY.unchecked, {
                          count: uncheckedCount,
                          rows: countParam(uncheckedCount),
                      })
                    : t(HELPER_KEY[blocker ?? 'ready'])}
                {describesSuggestedFixes
                    ? ` ${t(HELPER_KEY.suggestedFixes)}`
                    : null}
            </Typography>
        </div>
    );
};
